import OpenSeaDragon from "openseadragon";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createOSDAnnotator } from "@annotorious/openseadragon";
import "@annotorious/openseadragon/annotorious-openseadragon.css";
import { ApprovalImageToolbarModes, MarkupEventTypes } from "utils/constants";
import ImageControlToolbar from "../ImageControlToolbar";
import { useEffectOnce } from "utils/helper";
import { connect } from "react-redux";
import { logger } from "utils/logger";
import { Textarea, Button, Box, Group, ActionIcon } from "@mantine/core";
import classes from "./OpenSeaDragonViewer.module.scss";
import { IconTrash } from "@tabler/icons-react";

const OpenSeaDragonViewer = ({
  image,
  imageControlSettings,
  settings,
  onSettingsChange,
  setMarkup,
  fetchMarkups,
  user,
}) => {
  const [viewer, setViewer] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [comment, setComment] = useState("");
  const anno = useRef(null);
  const annotations = useRef([]);
  const zoomWrapper = useRef(null);
  const controlRef = useRef(null);
  const initRef = useRef(false);
  const commentPopoverRef = useRef(null);
  const viewerRef = useRef(null);
  const selectedAnnotationRef = useRef(null);
  const textareaRef = useRef(null);
  const ZOOM_FACTOR = 0.5;

  // Handle zoom controls
  useEffect(() => {
    if (!viewer || !viewer.viewport) return;

    if (
      imageControlSettings.zoomDirection ===
      ApprovalImageToolbarModes.ZOOM_RESET
    ) {
      viewer.viewport.goHome();
    } else {
      const isZoomIn =
        imageControlSettings.zoomDirection ===
        ApprovalImageToolbarModes.ZOOM_IN;
      const currentZoom = viewer.viewport.getZoom();
      const newZoom = isZoomIn
        ? currentZoom + ZOOM_FACTOR
        : currentZoom - ZOOM_FACTOR;

      if (newZoom < 0.5) {
        viewer.viewport.goHome();
      } else {
        viewer.viewport.zoomTo(newZoom, null, false);
      }
    }
  }, [imageControlSettings, viewer]);

  // Handle image changes and annotations
  useEffect(() => {
    if (!image || !viewer || !image.hiURL) return;

    const imageMatch = (url1, url2) => {
      return url1?.split("?")[0] === url2?.split("?")[0];
    };

    const currentImageUrl = image.hiURL;
    const needsUpdate = !imageMatch(imgUrl, currentImageUrl);

    if (needsUpdate && currentImageUrl && currentImageUrl.trim() !== "") {
      logger.log("Image change:", image);

      try {
        viewer.open({
          type: "image",
          url: currentImageUrl,
        });
        setImgUrl(currentImageUrl);
      } catch (error) {
        logger.log("Error opening image in viewer:", error);
      }
    }

    // Update annotations
    if (anno.current) {
      anno.current.clearAnnotations();
      anno.current.cancelSelected();

      if (image.annotations && image.annotations.length > 0) {
        image.annotations.forEach((annotation) => {
          anno.current.addAnnotation(annotation.payload);
        });
      }
    }

    annotations.current = image.annotations ? [...image.annotations] : [];
  }, [image, viewer, imgUrl]);

  // Initialize OpenSeaDragon viewer and Annotorious
  const initViewer = useCallback(() => {
    if (viewer) {
      if (viewer.zoomInButton?.tracker) viewer.zoomInButton.tracker.destroy();
      if (viewer.zoomOutButton?.tracker) viewer.zoomOutButton.tracker.destroy();
      if (viewer.zoomInButton) viewer.zoomInButton.destroy();
      viewer.destroy();
      if (controlRef.current) controlRef.current.remove();
    }

    viewerRef.current = null;

    const container = document.getElementById("openSeaDragon");

    if (
      !container ||
      container.offsetHeight === 0 ||
      container.offsetWidth === 0
    ) {
      logger.log("Container not ready, retrying...");
      setTimeout(initViewer, 100);
      return;
    }

    if (!image || !image.hiURL) {
      logger.log("Image not available, retrying...");
      setTimeout(initViewer, 100);
      return;
    }

    logger.log("Initializing viewer...");

    const updatePopoverPosition = (annotation) => {
      if (!viewerRef.current || !annotation?.target?.selector) return;

      const currentViewer = viewerRef.current;
      const selector = annotation.target.selector;
      if (selector.geometry) {
        const geo = selector.geometry;

        // Handle both geometry formats (simple x,y,w,h or bounds object)
        let x, y, width, height;
        if (geo.bounds) {
          // Bounds format
          x = geo.x || geo.bounds.minX || 0;
          y = geo.y || geo.bounds.minY || 0;
          width = geo.w || geo.bounds.maxX - geo.bounds.minX || 0;
          height = geo.h || geo.bounds.maxY - geo.bounds.minY || 0;
        } else {
          // Simple format
          x = geo.x || 0;
          y = geo.y || 0;
          width = geo.w || geo.width || 0;
          height = geo.h || geo.height || 0;
        }

        const container = zoomWrapper.current;
        if (!container) return;

        const viewportBounds = currentViewer.viewport.getBounds();
        const containerRect = container.getBoundingClientRect();
        const scale = containerRect.width / viewportBounds.width;
        
        const POPOVER_WIDTH = 300;
        const POPOVER_HEIGHT = 200; // Estimated height
        
        // Calculate bottom position (default - below annotation)
        const pointBottom = currentViewer.viewport.imageToViewportCoordinates(
          new OpenSeaDragon.Point(x + width / 2, y + height)
        );
        const leftBottom = (pointBottom.x - viewportBounds.x) * scale;
        const topBottom = (pointBottom.y - viewportBounds.y) * scale + 10;
        
        // Calculate top position (alternative - above annotation)
        const pointTop = currentViewer.viewport.imageToViewportCoordinates(
          new OpenSeaDragon.Point(x + width / 2, y)
        );
        const leftTop = (pointTop.x - viewportBounds.x) * scale;
        const topTop = (pointTop.y - viewportBounds.y) * scale - POPOVER_HEIGHT - 10;

        // Check if bottom position is within container (with some margin)
        const bottomFits = topBottom + POPOVER_HEIGHT + 20 <= containerRect.height;
        
        let finalLeft, finalTop;
        
        if (bottomFits) {
          // Use bottom position
          finalLeft = leftBottom;
          finalTop = topBottom;
        } else {
          // Use top position
          finalLeft = leftTop;
          finalTop = topTop;
        }
        
        // Center the popover horizontally on the annotation
        const centerLeft = finalLeft - POPOVER_WIDTH / 2;
        
        // Constrain horizontally to stay within container
        const constrainedLeft = Math.max(20, Math.min(centerLeft, containerRect.width - POPOVER_WIDTH - 20));

        // Constrain vertically to stay within container
        const constrainedTop = Math.max(20, Math.min(finalTop, containerRect.height - POPOVER_HEIGHT));

        setPopoverPosition({
          left: constrainedLeft,
          top: constrainedTop,
        });
      }
    };

    const showCommentPopover = (annotation) => {
      setSelectedAnnotation(annotation);
      selectedAnnotationRef.current = annotation;

      // Get comment from annotation bodies
      const commentBody = annotation.bodies?.find(
        (b) => b.purpose === "commenting"
      );
      setComment(commentBody?.value || "");

      // Calculate popover position
      updatePopoverPosition(annotation);

      // set focus to teh textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 200);
    };

    const newViewer = OpenSeaDragon({
      id: "openSeaDragon",
      prefixUrl: "openseadragon/images/",
      animationTime: 0.2,
      blendTime: 0.1,
      constrainDuringPan: false,
      maxZoomPixelRatio: 5,
      visibilityRatio: 1,
      zoomPerScroll: 1.1,
      gestureSettingsMouse: {
        clickToZoom: false,
        dragToPan: true,
      },
      tileSources: {
        type: "image",
        url: image.hiURL,
      },
      zoomInButton: "openseadragon-zoom-in",
      zoomOutButton: "openseadragon-zoom-out",
      homeButton: "openseadragon-home",
    });

    setViewer(newViewer);
    viewerRef.current = newViewer;

    // Initialize Annotorious
    const annotatorConfig = {
      crosshair: false,
      widgets: [{ widget: "COMMENT" }],
      drawingEnabled: settings.isCommenting,
      drawingTool: settings.annotationType?.toLowerCase() || 'rect', // 'rect' or 'polygon'
    };

    const annotator = createOSDAnnotator(newViewer, annotatorConfig);
    anno.current = annotator;

    // Set initial drawing state
    annotator.setDrawingEnabled(settings.isCommenting);
    
    // Set the drawing tool based on annotationType
    if (settings.annotationType) {
      const tool = settings.annotationType.toLowerCase();
      if (tool === 'polygon' || tool === 'rectangle') {
        annotator.setDrawingTool(tool);
      }
    }

    // Event handlers
    annotator.on("createAnnotation", (annotation) => {
      logger.log("Annotation created:", annotation);
      setMarkup(
        MarkupEventTypes.ADD_MARKUP,
        image,
        { payload: annotation },
        user
      );
      // After creating an annotation, re-enable drawing for the next one
      if (settings.isCommenting) {
        setTimeout(() => {
          if (annotator) {
            annotator.setDrawingEnabled(true);
          }
        }, 100);
      }

      showCommentPopover(annotation);
    });

    annotator.on("updateAnnotation", (annotation) => {
      logger.log("Annotation updated:", annotation);
      const existingAnnotation = annotations.current.find(
        (item) => item.payload.id === annotation.id
      );
      const updatedData = { ...existingAnnotation, payload: annotation };
      setMarkup(MarkupEventTypes.UPDATE_MARKUP, image, updatedData, user);
    });

    annotator.on("selectAnnotation", (annotation) => {
      logger.log("Annotation selected:", annotation);
      // Ensure drawing is disabled when annotation is selected
      annotator.setDrawingEnabled(false);
    });

    annotator.on("clickAnnotation", (annotation) => {
      logger.log("Annotation clicked");
      // Ensure drawing is disabled when clicking existing annotations
      annotator.setDrawingEnabled(false);

      showCommentPopover(annotation);
    });

    annotator.on("deleteAnnotation", () => {
      logger.log("Annotation deleted");
      // Hide popover
      setSelectedAnnotation(null);
      selectedAnnotationRef.current = null;
      setComment("");
      // Re-enable drawing after deletion if commenting mode is on
      if (settings.isCommenting) {
        annotator.setDrawingEnabled(true);
      }
    });

    // Listen to viewport changes to update popover position
    newViewer.addHandler("viewport-change", () => {
      if (selectedAnnotationRef.current) {
        updatePopoverPosition(selectedAnnotationRef.current);
      }
    });

    // Move controls to controller column
    const controllerCol = document.getElementById("controllerCol");
    if (controllerCol && controlRef.current) {
      controllerCol.append(controlRef.current);
    }
  }, [image, settings.isCommenting, setMarkup, user]);

  // Initialize on mount
  useEffectOnce(() => {
    if (!initRef.current) {
      initViewer();
      initRef.current = true;
    }

    return () => {
      if (viewer) {
        if (viewer.zoomInButton?.tracker) viewer.zoomInButton.tracker.destroy();
        if (viewer.zoomOutButton?.tracker)
          viewer.zoomOutButton.tracker.destroy();
        if (viewer.zoomInButton) viewer.zoomInButton.destroy();
        viewer.destroy();
      }

      viewerRef.current = null;

      // Clean up controls
      const controls = document.getElementsByClassName("openseacontrols");
      if (controls) {
        Array.from(controls).forEach((control) => control.remove());
      }
    };
  });

  // Update drawing state when settings change
  useEffect(() => {
    if (anno.current) {
      anno.current.setDrawingEnabled(settings.isCommenting);
    }
  }, [settings.isCommenting]);

  // Update drawing tool when annotationType changes
  useEffect(() => {
    if (anno.current && settings?.annotationType) {
      const tool = settings.annotationType.toLowerCase();
      if (tool === 'polygon' || tool === 'rectangle') {
        anno.current.setDrawingTool(tool);
      }
    }
  }, [settings?.annotationType]);

  const handleSettingsChange = useCallback(
    (e, data) => {
      onSettingsChange(e, data);
    },
    [onSettingsChange]
  );

  // Handle comment submission
  const handleCommentOk = useCallback(() => {
    if (selectedAnnotation && anno.current) {
      const updatedAnnotation = {
        ...selectedAnnotation,
        bodies: [
          ...(selectedAnnotation.bodies || []).filter(
            (b) => b.purpose !== "commenting"
          ),
          {
            type: "TextualBody",
            purpose: "commenting",
            value: comment,
          },
        ],
      };
      anno.current.updateAnnotation(updatedAnnotation);

      // Also save via setMarkup
      const existingAnnotation = annotations.current.find(
        (item) => item.payload.id === updatedAnnotation.id
      );
      const updatedData = {
        ...existingAnnotation,
        payload: updatedAnnotation,
      };
      setMarkup(MarkupEventTypes.UPDATE_MARKUP, image, updatedData, user);

      setSelectedAnnotation(null);
      selectedAnnotationRef.current = null;
      setComment("");
      logger.log("Comment added:", comment);
    }
  }, [selectedAnnotation, comment, image.id, setMarkup, user]);

  // Handle comment cancellation
  const handleCommentCancel = useCallback(() => {
    setSelectedAnnotation(null);
    selectedAnnotationRef.current = null;
    setComment("");
    anno.current.setDrawingEnabled(true);
  }, []);

  // Handle clicks on empty space to cancel selection and re-enable drawing
  const handleContainerClick = useCallback(
    (e) => {
      if (!anno.current || !settings.isCommenting) return;

      const target = e.target;
      // Check if click is on annotation, handle, editor, popover, or annotation layer
      const isAnnotation = target.closest(".a9s-annotation");
      const isSelectionHandle = target.closest(".a9s-handle");
      const isEditor = target.closest(".r6o-editor");
      const isPopover = target.closest(".comment-popover");
      const isAnnotationLayer = target.classList.contains(
        "a9s-osd-annotationlayer"
      );

      // Only process clicks on empty space (not on annotations or interactive elements)
      if (!isAnnotation && !isSelectionHandle && !isEditor && !isPopover) {
        // Small delay to let annotation events process first
        setTimeout(() => {
          if (anno.current && settings.isCommenting) {
            const hasSelection = document.querySelector(
              ".a9s-annotation.selected"
            );
            // Only cancel if no annotation is actually selected
            if (!hasSelection) {
              setSelectedAnnotation(null);
              selectedAnnotationRef.current = null;
              setComment("");
              anno.current.setDrawingEnabled(true);
            }
          }
        }, 100);
      }
    },
    [settings.isCommenting]
  );

  return (
    <div
      ref={zoomWrapper}
      style={{
        height: "100%",
        width: "100%",
        minHeight: "400px",
        position: "relative",
      }}
    >
      <div ref={controlRef} id="openseacontroller" className="openseacontrols">
        <ImageControlToolbar
          settingsChange={handleSettingsChange}
          settings={settings}
        />
      </div>

      <div
        className={`${classes.container} ${
          settings.isCommenting ? "annotating" : ""
        }`}
        id="openSeaDragon"
        style={{
          height: "100%",
          width: "100%",
          minHeight: "400px",
        }}
      />

      {/* Comment Popover */}
      {selectedAnnotation && (
        <Box
          ref={commentPopoverRef}
          className={classes.commentPopover}
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <Textarea
            ref={textareaRef}
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autosize
            minRows={2}
            maxRows={6}
            style={{
              marginBottom: "12px",
              backgroundColor: "#1a1a1a",
              color: "#fff",
            }}
          />
          <Group
            justify="space-between"
            align="stretch"
            
            style={{
              width: "100%",
            }}
          >
            <ActionIcon size="md" variant="default" onClick={handleCommentCancel}>
              <IconTrash size={16} />
            </ActionIcon>

            <Group>
              <Button size="xs" variant="default" onClick={handleCommentCancel}>
                Cancel
              </Button>
              <Button
                size="xs"
                variant="filled"
                onClick={() => handleCommentOk()}
              >
                Ok
              </Button>
            </Group>
          </Group>
        </Box>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(OpenSeaDragonViewer);
